import React from 'react';
import { Link, useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowRight, BookOpen, FileText, Wrench, Layers, CheckCircle2 } from 'lucide-react';
import { Seo, Breadcrumbs, MarketingShell, CtaInline, CtaBlock, RelatedList, breadcrumbLd, PageHero, Band } from '../../seo/kit';
import {
  DrawReadyPreSubmissionCheck, DrawProcessVisual, DrawReadyReliefVisual, AspirinInlineCta, EndArticleCta,
} from '../../seo/components';
import {
  SITE, AUTHOR, OG_IMAGE, PRIMARY_CTA, DEMO_CTA, CLUSTERS, PRODUCTS, ARTICLES, GLOSSARY,
  getArticle, getProduct, getTerm, articlesByCluster, relatedArticles,
  isPublished, articlesBySlugs, DRAW_PROCESS_CLUSTER, AUTHORITY_PAGE,
} from '../../seo/data';

const fmt = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

// ---------------- Resources hub ----------------
export function ResourcesHub() {
  const crumbs = [{ label: 'Home', href: '/' }, { label: 'Resources', href: '/resources' }];
  const sections = [
    ...Object.values(CLUSTERS).map((c) => ({ label: c.label, href: c.path, dek: c.dek, icon: FileText })),
    { label: 'Free Tools & Templates', href: '/tools', dek: 'Checklists and templates that make your next draw bank-ready.', icon: Wrench },
    { label: 'DrawReady Glossary', href: '/glossary', dek: 'Plain-English definitions for every construction draw term.', icon: BookOpen },
  ];
  return (
    <MarketingShell>
      <Seo title="DrawReady Resources: Construction Draws, Loans, Lien Waivers & Budgets" description="Guides for builders, developers, GCs and lenders on construction draws, construction loans, lien waivers, and budgeting — so your next draw is bank-ready." path="/resources" jsonLd={breadcrumbLd(crumbs)} />
      <PageHero top={<Breadcrumbs items={crumbs} />} title="Everything you need to get a draw funded">
        Missing one document can delay your draw for weeks. These guides show builders, developers and lenders exactly how to build a draw your bank approves the first time.
      </PageHero>
      <Band bg="#fff">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sections.map((s) => (
            <Link key={s.href} to={s.href} className="rounded-lg border p-6 transition" style={{ borderColor: '#D8DEE8' }} data-testid={`hub-${s.href}`}>
              <s.icon className="w-6 h-6" style={{ color: '#1E4ED8' }} />
              <div className="font-bold mt-3" style={{ color: '#13294B' }}>{s.label}</div>
              <div className="text-sm mt-1" style={{ color: '#5B6678' }}>{s.dek}</div>
              <div className="text-sm font-semibold mt-3" style={{ color: '#1E4ED8' }}>Explore</div>
            </Link>
          ))}
        </div>
      </Band>
      <Band bg="#F2F4F7"><CtaBlock /></Band>
    </MarketingShell>
  );
}

// ---------------- Cluster hub (4 routes) ----------------
export function ClusterHub({ cluster: fixedCluster }) {
  const params = useParams();
  const cluster = fixedCluster || params.cluster;
  const c = CLUSTERS[cluster];
  if (!c) return <Navigate to="/resources" replace />;
  const arts = articlesByCluster(cluster);
  const crumbs = [{ label: 'Home', href: '/' }, { label: 'Resources', href: '/resources' }, { label: c.label, href: c.path }];
  return (
    <MarketingShell>
      <Seo title={`${c.label} — DrawReady Resources`} description={c.dek} path={c.path} jsonLd={breadcrumbLd(crumbs)} />
      <PageHero top={<Breadcrumbs items={crumbs} />} title={c.h1}>
        {c.dek}
        <div className="mt-4"><Link to={c.product} className="text-sm font-semibold" style={{ color: '#1E4ED8' }}>See how DrawReady helps</Link></div>
      </PageHero>
      <Band bg="#fff">
        <div className="grid sm:grid-cols-2 gap-4">
          {arts.length ? arts.map((a) => (
            <Link key={a.slug} to={`/resources/${a.slug}`} className="rounded-lg border p-5 transition" style={{ borderColor: '#D8DEE8' }}>
              <div className="text-[13px] font-semibold" style={{ color: '#1E4ED8' }}>{a.category}</div>
              <div className="font-semibold mt-1 text-lg" style={{ color: '#13294B' }}>{a.h1}</div>
              <div className="text-sm mt-1" style={{ color: '#5B6678' }}>{a.dek}</div>
            </Link>
          )) : <p style={{ color: '#5B6678' }}>New guides are coming soon.</p>}
        </div>
      </Band>
      <Band bg="#F2F4F7"><CtaBlock /></Band>
    </MarketingShell>
  );
}

// ---------------- Article body renderer ----------------
function Block({ b }) {
  if (b.t === 'p') return <p className="text-slate-700 leading-relaxed my-4">{b.v}</p>;
  if (b.t === 'h2') return <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-2">{b.v}</h2>;
  if (b.t === 'h3') return <h3 className="text-xl font-bold text-slate-900 mt-6 mb-2">{b.v}</h3>;
  if (b.t === 'steps') return <ol className="list-decimal pl-6 my-4 space-y-1.5 text-slate-700">{b.v.map((x, i) => <li key={i}>{x}</li>)}</ol>;
  if (b.t === 'callout') return <div className="my-6 rounded-xl border-l-4 border-blue-500 bg-blue-50/60 p-4 text-slate-800 font-medium">{b.v}</div>;
  if (b.t === 'table') return (
    <div className="my-6 overflow-x-auto"><table className="w-full text-sm border border-slate-200 rounded-lg"><thead><tr className="bg-slate-50 text-left">{b.head.map((h) => <th key={h} className="px-3 py-2 font-semibold text-slate-700 border-b border-slate-200">{h}</th>)}</tr></thead><tbody>{b.rows.map((r, i) => <tr key={i} className="border-b border-slate-100 last:border-0">{r.map((c, j) => <td key={j} className="px-3 py-2 text-slate-600">{c}</td>)}</tr>)}</tbody></table></div>
  );
  if (b.t === 'faq') return (
    <div className="my-6"><h2 className="text-2xl font-bold text-slate-900 mb-3">Frequently asked questions</h2>{b.v.map(([q, a], i) => <div key={i} className="border-b border-slate-100 py-3"><div className="font-semibold text-slate-900">{q}</div><div className="text-slate-600 mt-1">{a}</div></div>)}</div>
  );
  // Embeddable reusable components (placed by the CMS via body block types).
  if (b.t === 'presubmission-check') return <DrawReadyPreSubmissionCheck />;
  if (b.t === 'process-visual') return <DrawProcessVisual />;
  if (b.t === 'relief-visual') return <DrawReadyReliefVisual />;
  if (b.t === 'aspirin-cta') return <AspirinInlineCta />;
  if (b.t === 'end-cta') return <EndArticleCta />;
  // Contextual internal link to the primary authority page. Optional anchor.
  if (b.t === 'authority-link') {
    const anchor = b.anchor || 'construction draw management software';
    return (
      <p className="text-slate-700 leading-relaxed my-4">
        {b.v ? b.v + ' ' : ''}
        <Link to={AUTHORITY_PAGE} className="text-blue-600 font-semibold hover:underline" data-testid="authority-link">{anchor}</Link>
        {b.post ? ' ' + b.post : ''}
      </p>
    );
  }
  return null;
}

// ---------------- Article template (/resources/:slug) ----------------
export function ArticlePage() {
  const { slug } = useParams();
  const a = getArticle(slug);
  // Draft articles are not public: redirect direct visits to the hub.
  if (!a || !isPublished(a)) return <Navigate to="/resources" replace />;
  const c = CLUSTERS[a.cluster];
  const crumbs = [{ label: 'Home', href: '/' }, { label: 'Resources', href: '/resources' }, { label: c.label, href: c.path }, { label: a.h1, href: `/resources/${a.slug}` }];
  const faq = a.body.find((b) => b.t === 'faq');
  const ogImage = a.featuredImage || OG_IMAGE;
  const jsonLd = [
    breadcrumbLd(crumbs),
    {
      '@context': 'https://schema.org', '@type': 'Article', headline: a.h1,
      description: a.dek, image: (a.featuredImage && a.featuredImage.startsWith('http')) ? a.featuredImage : SITE + ogImage,
      datePublished: a.published, dateModified: a.updated,
      author: { '@type': 'Organization', name: AUTHOR }, publisher: { '@type': 'Organization', name: 'DrawReady', logo: { '@type': 'ImageObject', url: SITE + '/logo-full.png' } },
      mainEntityOfPage: SITE + `/resources/${a.slug}`,
    },
    // Only emit FAQ schema when the CMS explicitly opts in AND an FAQ exists.
    (a.faqSchema && faq) && { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: faq.v.map(([q, ans]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: ans } })) },
  ].filter(Boolean);
  const mid = Math.floor(a.body.length / 2);
  const related = (a.related && a.related.length) ? articlesBySlugs(a.related) : relatedArticles(a.cluster, a.slug);
  return (
    <MarketingShell>
      <Seo title={a.title} description={a.meta} path={`/resources/${a.slug}`} type="article" image={ogImage} jsonLd={jsonLd} />
      <section style={{ background: '#EEF2F8' }}>
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <Breadcrumbs items={crumbs} />
          <div className="text-[13px] font-semibold mt-4" style={{ color: '#1E4ED8' }}>{a.category}</div>
          <h1 className="text-4xl md:text-5xl mt-1" style={{ color: '#13294B', fontWeight: 800, letterSpacing: '-0.02em' }}>{a.h1}</h1>
          <p className="text-lg mt-3" style={{ color: '#5B6678' }}>{a.dek}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mt-4" style={{ color: '#98A2B3' }}>
            <span>By <span className="font-medium" style={{ color: '#5B6678' }}>{AUTHOR}</span></span>
            <span>Published {fmt(a.published)}</span>
            <span>Updated {fmt(a.updated)}</span>
            <span>{a.minutes} min read</span>
          </div>
        </div>
      </section>
      <article className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
        {a.featuredImage && (
          <img src={a.featuredImage} alt={a.imageAlt || a.h1} className="w-full rounded-2xl border border-slate-200 mt-6 object-cover" data-testid="article-featured-image" />
        )}
        <div className="mt-4">
          {a.body.slice(0, mid).map((b, i) => <Block key={i} b={b} />)}
          <CtaInline />
          {a.body.slice(mid).map((b, i) => <Block key={i} b={b} />)}
        </div>
        <div className="mt-8 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm">
          <span className="text-slate-600">Related product: </span>
          <Link to={c.product} className="font-semibold hover:underline" style={{ color: '#1E4ED8' }}>{c.label} software</Link>
          <span className="text-slate-400"> · </span>
          <Link to={AUTHORITY_PAGE} className="font-semibold text-blue-600 hover:underline" data-testid="article-authority-link">construction draw management software</Link>
        </div>
        <RelatedList items={related} />
        <CtaBlock />
      </article>
    </MarketingShell>
  );
}

// ---------------- Product / solution page (8 routes) ----------------
export function ProductPage({ slug: fixedSlug }) {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const p = getProduct(slug);
  if (!p) return <Navigate to="/" replace />;
  const c = CLUSTERS[p.cluster];
  const crumbs = [{ label: 'Home', href: '/' }, { label: p.name, href: `/${p.slug}` }];
  const jsonLd = [
    breadcrumbLd(crumbs),
    {
      '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: p.name,
      applicationCategory: 'BusinessApplication', operatingSystem: 'Web, iOS, Android',
      description: p.meta, image: SITE + OG_IMAGE, url: SITE + `/${p.slug}`,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: '30-day free trial' },
      publisher: { '@type': 'Organization', name: 'DrawReady' },
    },
  ];
  return (
    <MarketingShell>
      <Seo title={p.title} description={p.meta} path={`/${p.slug}`} jsonLd={jsonLd} />
      <PageHero top={<Breadcrumbs items={crumbs} />} eyebrow={`For ${p.audience}`} title={p.h1}>
        <div className="grid md:grid-cols-3 gap-5">
          <div className="rounded-lg border p-5 bg-white" style={{ borderColor: '#D8DEE8' }}><div className="text-sm font-semibold" style={{ color: '#5B6678' }}>The problem</div><p className="mt-1.5 text-[15px]" style={{ color: '#1D2939' }}>{p.pain}</p></div>
          <div className="rounded-lg border p-5 bg-white" style={{ borderColor: '#D8DEE8' }}><div className="text-sm font-semibold" style={{ color: '#5B6678' }}>What it costs you</div><p className="mt-1.5 text-[15px]" style={{ color: '#1D2939' }}>{p.consequence}</p></div>
          <div className="rounded-lg border p-5 bg-white" style={{ borderColor: '#D8DEE8' }}><div className="text-sm font-semibold" style={{ color: '#1F7A4F' }}>The relief</div><p className="mt-1.5 text-[15px]" style={{ color: '#1D2939' }}>{p.relief}</p></div>
        </div>
        <div className="flex flex-wrap gap-3 mt-8">
          <Link to={PRIMARY_CTA.href} className="btn-primary">{PRIMARY_CTA.label}</Link>
          <Link to={DEMO_CTA.href} className="btn-secondary">{DEMO_CTA.label}</Link>
        </div>
      </PageHero>

      <Band bg="#fff">
        <h2 className="text-2xl md:text-3xl" style={{ color: '#13294B', fontWeight: 700 }}>How DrawReady helps</h2>
        <ul className="mt-5 space-y-3 max-w-2xl">
          {p.features.map((f) => <li key={f} className="flex items-start gap-3 text-[17px]" style={{ color: '#1D2939' }}><span className="mt-2.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#13294B' }} />{f}</li>)}
        </ul>
      </Band>

      <Band bg="#F2F4F7">
        <div className="rounded-lg border p-5 bg-white" style={{ borderColor: '#D8DEE8' }}>
          <div className="font-semibold" style={{ color: '#13294B' }}>Learn the ropes</div>
          <p className="text-[15px] mt-1" style={{ color: '#5B6678' }}>Guides that go with this: <Link to={c.path} className="font-medium hover:underline" style={{ color: '#1E4ED8' }}>{c.label} resources</Link></p>
          <div className="flex flex-wrap gap-2 mt-3">
            {articlesByCluster(p.cluster).slice(0, 3).map((a) => <Link key={a.slug} to={`/resources/${a.slug}`} className="text-[13px] font-medium rounded-md px-3 py-1.5" style={{ color: '#1E4ED8', background: '#fff', border: '1px solid #D8DEE8' }}>{a.h1}</Link>)}
          </div>
        </div>
      </Band>
      {p.slug === 'construction-draw-management-software' && (() => {
        const cluster = articlesBySlugs(DRAW_PROCESS_CLUSTER);
        if (!cluster.length) return null;
        return (
          <Band bg="#fff">
            <div data-testid="learn-draw-process">
              <h2 className="text-2xl md:text-3xl" style={{ color: '#13294B', fontWeight: 700 }}>Learn the Construction Draw Process</h2>
              <p className="mt-1 max-w-2xl" style={{ color: '#5B6678' }}>Go deeper on how construction draws work, what documents you need, and how to submit a draw your lender can fund.</p>
              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                {cluster.map((a) => (
                  <Link key={a.slug} to={`/resources/${a.slug}`} className="rounded-lg border p-5 hover:opacity-90 transition" style={{ borderColor: '#D8DEE8' }} data-testid={`learn-card-${a.slug}`}>
                    <div className="text-[13px] font-semibold" style={{ color: '#1E4ED8' }}>{a.category}</div>
                    <div className="font-semibold mt-1" style={{ color: '#13294B' }}>{a.h1}</div>
                    <div className="text-[15px] mt-1 line-clamp-2" style={{ color: '#5B6678' }}>{a.dek}</div>
                  </Link>
                ))}
              </div>
            </div>
          </Band>
        );
      })()}
      <Band bg="#F2F4F7"><CtaBlock /></Band>
    </MarketingShell>
  );
}

// ---------------- Glossary ----------------
export function GlossaryIndex() {
  const crumbs = [{ label: 'Home', href: '/' }, { label: 'Glossary', href: '/glossary' }];
  return (
    <MarketingShell>
      <Seo title="DrawReady Glossary: Construction Draw Terms, Explained" description="Plain-English definitions for construction draws, draw requests, lien waivers, retainage, draw schedules, and more construction loan terms." path="/glossary" jsonLd={breadcrumbLd(crumbs)} />
      <PageHero top={<Breadcrumbs items={crumbs} />} title="DrawReady Glossary">
        Every construction draw term, in plain English — so nothing on your draw is a mystery.
      </PageHero>
      <Band bg="#fff">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {GLOSSARY.map((g) => (
            <Link key={g.slug} to={`/glossary/${g.slug}`} className="rounded-lg border p-5 transition hover:opacity-90" style={{ borderColor: '#D8DEE8' }}>
              <div className="font-bold" style={{ color: '#13294B' }}>{g.term}</div>
              <div className="text-sm mt-1" style={{ color: '#5B6678' }}>{g.short}</div>
            </Link>
          ))}
        </div>
      </Band>
      <Band bg="#F2F4F7"><CtaBlock /></Band>
    </MarketingShell>
  );
}

export function GlossaryTerm() {
  const { term } = useParams();
  const g = getTerm(term);
  if (!g) return <Navigate to="/glossary" replace />;
  const crumbs = [{ label: 'Home', href: '/' }, { label: 'Glossary', href: '/glossary' }, { label: g.term, href: `/glossary/${g.slug}` }];
  const jsonLd = [breadcrumbLd(crumbs), { '@context': 'https://schema.org', '@type': 'DefinedTerm', name: g.term, description: g.long, inDefinedTermSet: SITE + '/glossary' }];
  const c = CLUSTERS[g.cluster];
  return (
    <MarketingShell>
      <Seo title={`${g.term} — DrawReady Glossary`} description={g.short} path={`/glossary/${g.slug}`} jsonLd={jsonLd} />
      <PageHero top={<Breadcrumbs items={crumbs} />} title={g.term}>
        {g.long}
      </PageHero>
      <Band bg="#fff">
        {c && <div className="text-sm">Related: <Link to={c.path} className="font-medium hover:underline" style={{ color: '#1E4ED8' }}>{c.label} resources →</Link></div>}
        <div className="mt-8"><CtaBlock /></div>
      </Band>
    </MarketingShell>
  );
}

// ---------------- Tools ----------------
export function ToolsPage() {
  const crumbs = [{ label: 'Home', href: '/' }, { label: 'Tools', href: '/tools' }];
  const tools = [
    { name: 'Draw Readiness Checklist', dek: 'Score your draw against the 7 things your bank checks — before a missing doc stalls funding.', href: '/tools/draw-readiness-checklist', live: true },
    { name: 'Loan Interest Estimator', dek: 'See what your draws actually cost in interest — and how drawing too early eats your budget.', href: '/tools/draw-cost-estimator', live: true },
    { name: 'Draw Schedule Builder', dek: 'Map your draws and see how much each funds after retainage — so you don’t run short mid-build.', href: '/tools/draw-schedule-builder', live: true },
  ];
  return (
    <MarketingShell>
      <Seo title="Free Construction Draw Tools & Templates | DrawReady" description="Free checklists, calculators, and templates for construction draws, lien waivers, and budgets — so your next draw is bank-ready before you submit." path="/tools" jsonLd={breadcrumbLd(crumbs)} />
      <PageHero top={<Breadcrumbs items={crumbs} />} title="Free tools & templates">
        A missing document or a short draw can stall your whole job. Start with a free tool — then let DrawReady check the entire draw before your lender does.
      </PageHero>
      <Band bg="#fff">
        <div className="grid sm:grid-cols-3 gap-4">
          {tools.map((t) => (
            <Link key={t.name} to={t.href} className="block rounded-lg border p-6 transition hover:opacity-90" style={{ borderColor: '#D8DEE8' }} data-testid={`tool-card-${t.href}`}>
              <Wrench className="w-6 h-6" style={{ color: '#1E4ED8' }} />
              <div className="font-bold mt-3" style={{ color: '#13294B' }}>{t.name}</div>
              <div className="text-sm mt-1" style={{ color: '#5B6678' }}>{t.dek}</div>
              <span className="inline-flex items-center gap-1 text-sm font-semibold mt-3" style={{ color: '#1E4ED8' }}>{t.live ? 'Open the tool' : 'Get it in DrawReady'} <ArrowRight className="w-4 h-4" /></span>
            </Link>
          ))}
        </div>
      </Band>
      <Band bg="#F2F4F7"><CtaBlock /></Band>
    </MarketingShell>
  );
}
